import { type QueryKey, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import type { Parameters, Response } from '../../../../../types/typeHelper';
import { ERROR_DIALOG } from '../../../../constants/errorDialogConstants';
import { throwError, useHandleQueryError } from '../../../../helper/errorMessages';
import { trackGAEvent } from '../../../../slices/googleAnalytics/googleAnalyticsThunks';
import { PROCESSES_DEFAULT_ERROR_MESSAGE } from '../../../../slices/processes/processesConstants';
import { useApiClient } from '../../client';
import {
  type FetchProcessesFilterSuggestionsData,
  type FetchProcessesResponseData,
  getGetProcessDetailsQueryKey,
  getGetProcessesFilterSuggestionsQueryKey,
  getGetProcessesQueryKey,
} from './processes-api-helpers';

interface UseGetProcessesProps {
  query: Omit<Parameters<'/chat/processes', 'get'>['query'], 'page'>;
}

export function useGetProcesses({ query }: UseGetProcessesProps) {
  const client = useApiClient();

  return useInfiniteQuery<
    FetchProcessesResponseData | undefined,
    Error,
    FetchProcessesResponseData | undefined,
    QueryKey,
    number
  >({
    enabled: !!query.house_id,
    queryKey: getGetProcessesQueryKey(query),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || (lastPage && lastPage.length === 0)) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    queryFn: async ({ pageParam }) => {
      const { data, error } = await client.GET('/chat/processes', {
        params: {
          query: {
            ...query,
            page: pageParam,
          },
        },
      });
      if (!error) {
        return data;
      }
      // @ts-expect-error error type created by backend doesn't match error type in openapi-fetch
      const { error_code: errorCode, description }: { error_code: string; description?: string } = error;
      // all process errors (get_processes.*) are sent with a user facing description by the backend,
      // any other error gets the default ONLINE PLUS error message
      const errorMessage = (errorCode?.startsWith('get_processes.') && description) || PROCESSES_DEFAULT_ERROR_MESSAGE;

      return throwError(errorMessage, errorCode);
    },
    select: (data) => {
      const processes: FetchProcessesResponseData = [];
      if (!data?.pages) return processes;
      return data.pages.filter((page) => !!page).reduce((acc, page) => acc.concat(page), []);
    },
  });
}

interface UseGetProcessDetailsProps<T> {
  query: Parameters<'/chat/process-details', 'get'>['query'];
  select?: (data: Response<'/chat/process-details', 'get'> | undefined) => T;
}

export function useGetProcessDetails<T = Response<'/chat/process-details', 'get'>>({
  query,
  select,
}: UseGetProcessDetailsProps<T>) {
  const client = useApiClient();
  const handleQueryError = useHandleQueryError();
  return useQuery({
    enabled: !!query.house_id && !!query.process_number && !!query.suffix,
    queryKey: getGetProcessDetailsQueryKey(query),
    queryFn: async () => {
      const { data, error } = await client.GET('/chat/process-details', {
        params: {
          query,
        },
      });
      handleQueryError({
        errorDialog: ERROR_DIALOG.NONE,
        // @ts-expect-error mistatch between openapi-fetch error and backend error types
        error,
      });
      return data;
    },
    select,
  });
}

interface UseGetProcessesFilterSuggestionsProps {
  query: Parameters<'/chat/process-filter-suggestions', 'get'>['query'];
}

export function useGetProcessesFilterSuggestions({ query }: UseGetProcessesFilterSuggestionsProps) {
  const client = useApiClient();
  const dispatch = useDispatch();
  const handleQueryError = useHandleQueryError();
  return useQuery({
    enabled: !!query.house_id || !!query.query_string,
    queryKey: getGetProcessesFilterSuggestionsQueryKey(query),
    queryFn: async () => {
      const { data, error } = await client.GET('/chat/process-filter-suggestions', {
        params: {
          query,
        },
      });
      handleQueryError({
        errorDialog: ERROR_DIALOG.NONE,
        // @ts-expect-error mistatch between openapi-fetch error and backend error types
        error,
      });
      // Needs better way of handling events in react-query
      dispatch(trackGAEvent('MAGIC_CUBE', 'SEARCHED_ORDER'));
      return data;
    },
    select: (data) => {
      if (!data) return [];
      // deduplicate based on Suggestion + Type
      const seen: { [key: string]: boolean } = {};
      const uniqueSuggestions: FetchProcessesFilterSuggestionsData = [];
      for (const item of data) {
        const suggestionId = `${item.Suggestion}-${item.Type}`;
        if (seen[suggestionId] !== true) {
          seen[suggestionId] = true;
          uniqueSuggestions.push(item);
        }
      }
      return uniqueSuggestions;
    },
  });
}
